import { makeAutoObservable } from 'mobx';
import i18n from '../../../i18n';
import { DataAnalysis, Dish, Order } from '../../../models';
import apiManage from '../../../request';
import {
    applyHistoryOrderDetailAmountFallback,
    cloneOrderDetailTableRowFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
} from '../../../request/api/data-analysis';
import { validateCurrentDate, SortType, handleSort } from '../../../constants/tool-order';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';
import { resolveCloudSensitiveHistoryOrderHeadParams } from '../../../utils/cloud-sensitive-history-order-head-params';
import {
    isSensitiveShiftOperation,
} from '../../../utils/sensitive-shift-operation';
import moment from 'moment';
import GlobalNotification from '../../../component/common/snackbar';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import { exportExcel } from '../../../constants/tool.fn';

export {
    isHandOverPrintOnlyOperation,
    isSensitiveShiftOperation,
    SENSITIVE_OP_TYPE_HAND_OVER_PRINT_ONLY,
} from '../../../utils/sensitive-shift-operation';

/** 与敏感操作页 `operationType` 一致，用于导出列「操作名称」文案 */
const SENSITIVE_OPERATION_TYPE_I18N: Record<number, string> = {
    2: 'payment_text_dish_reprice_message',
    3: 'order_cart_page_text_dish_action_return',
    4: 'manager_text_refund',
    5: 'global_right_sidebar_switch_order_table',
    6: 'global_right_sidebar_merge_order',
    7: 'manager_text_summary_payment_details_refund',
    8: 'global_left_sidebar_manger_hand_over',
    10: 'global_panel_text_discount_money',
    11: 'manager_text_stop_critival_log',
    12: 'payment_text_keyboard_wipe',
    13: 'manager_text_use_coupons',
    14: 'manager_text_bind_vip',
    15: 'table_page_text_dismiss_table_message',
    18: 'order_cart_page_text_history_re_print_info',
    19: 'order_page_text_cancel_order',
    20: 'manager_text_gift_card_redeem_rollback',
    21: 'manager_text_recharge_balance_redeem_rollback',
    22: 'manager_text_delete_invoice',
    23: 'manager_text_hand_over_print_only',
    24: 'manager_text_download_sign_record',
    25: 'manager_text_view_sign_record',
};

class SensitiveDataStore {
    // loading
    loading: boolean = true;
    // 敏感操作记录（可经前端排序后的展示数据）
    data: DataAnalysis[] = [];
    /** 接口拉取的原始列表，用于清除排序时恢复顺序 */
    dataSource: DataAnalysis[] = [];
    // 与 refund-data 一致：当前列排序（ASC/DESC + 列 id）
    sortKeyValue: SortType = {};
    // 弹窗配置
    dialogConfig: {
        open: boolean,
        // 弹窗类型  1 老聚客  2 POS
        dialogType?: number;
        // 详情数据
        detail?: string
    } = {
            open: false,
        }
    // 是否成功获取了数据
    errorMessage: string = '';
    /** 列表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();
    // 详情弹窗
    isOpenOrderDetail = false;
    // 详情数据
    /** 打开详情时仅用于请求参数的订单标识 */
    orderDetailData: Order = {};
    /** 详情摘要：默认列表 stub/行，接口有值字段再覆盖 */
    orderDetailSummary: Order | null = null;
    // 详情loading
    detailLoading = true;
    // 详情菜品
    orderDetailDish: Dish[] = []

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * 获取数据
     */
    async fetchData(stringId: string, startDate: string, endDate: string, pageType: PageType) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.setLoading(true);
        try {
            let result;
            if (pageType === 'CLOUD') {
                result = await apiManage.get_critical_operations({
                    string_id: stringId,
                    start: startDate,
                    end: endDate,
                }, { cancelToken });
            } else {
                const { startTime, endTime } = validateCurrentDate();
                result = await apiManage.get_data_analysis_critical_operations({
                    string_id: stringId,
                    start: startTime,
                    end: endTime,
                }, { cancelToken });
            }
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (!result?.error) {
                this.setData(result);
                // 新数据到达后取消排序高亮，列表与接口顺序一致
                this.sortKeyValue = {};
                this.setErrorMessage('');
            } else {
                if (result.message !== 'canceled') {
                    if (result?.result?.statusCode === 404 && pageType === 'CLOUD') {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else if (result?.result?.statusCode === 404 && pageType === 'INSTANT') {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_no_database_config'));
                    } else {
                        this.setErrorMessage(i18n.t('business_analysis_page_get_critical_operations_failed'));
                    }
                    this.setData([]);
                }
            }
        } finally {
            this.listFetchGate.endLoading(seq, (value) => this.setLoading(value));
        }
    }


    /**
     * 获取订单详情：即时走 order_detail_with_head；云端走 history_order/head。
     * @param pageType INSTANT / CLOUD
     * @param periodStart 云端列表筛选开始（critical_operations/period 的 start）
     * @param periodEnd 云端列表筛选结束（critical_operations/period 的 end）
     */
    async fetchDetailData(pageType: PageType, periodStart = '', periodEnd = '') {
        this.setDetailLoading(true);
        const mainPageData = MobxObjectsManager.get(MainPageState);
        const stringId = mainPageData.currentRestaurant?.string_id || '';
        try {
            let res;
            if (pageType === 'CLOUD') {
                const headParams = resolveCloudSensitiveHistoryOrderHeadParams({
                    serverId: this.orderDetailData.server_id,
                    orderHeadId: this.orderDetailData.order_head_id,
                    start: periodStart,
                    end: periodEnd,
                });
                if (!headParams) {
                    GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
                    return;
                }
                res = await apiManage.get_data_analysis_res_history_order_details({
                    string_id: stringId,
                    ...headParams,
                    sort: 'ASC',
                });
            } else {
                res = await apiManage.get_data_analysis_order_details({
                    string_id: stringId,
                    order_head_id: `${this.orderDetailData.order_head_id || ''}`,
                    sort: 'ASC',
                });
            }
            const { order, details } = parseDataAnalysisOrderDetailWithHead(res);
            if (!res?.error) {
                const apiOrder =
                    pageType === 'CLOUD' ? applyHistoryOrderDetailAmountFallback(order) : order;
                this.setOrderDetailSummary(
                    mergeOrderDetailSummaryWithTableRow(this.orderDetailData, apiOrder)
                );
                this.setOrderDetailDish(details ?? []);
            } else {
                GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
            }
        } finally {
            this.setDetailLoading(false);
        }
    }

    /**
     * 前端排序：在 dataSource 上排序，与 refund-data 的 setSortValue 模式一致
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value
        if (value.key && value.value) {
            if (value.key === 'operation_time') {
                this.data = handleSort(value, [...this.dataSource], 'Date') || []
            } else if (value.key === 'operation_name') {
                this.data = handleSort(value, [...this.dataSource], 'String') || []
            } else if (value.key === 'order_head_id') {
                // 订单 ID 按数值排序（与接口字段 number 类型一致）
                this.data = handleSort(value, [...this.dataSource], 'Number') || []
            } else {
                this.data = [...this.dataSource]
            }
        } else {
            this.data = [...this.dataSource]
        }
    }

    // 设置弹窗参数
    setDialogConfig(value: typeof this.dialogConfig) {
        this.dialogConfig = value;
    }

    setData(value: DataAnalysis[]) {
        this.dataSource = [...(value || [])]
        this.data = [...(value || [])]
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }
    /**
     * 更新errorMessage
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    /**
    * 打开详情弹窗
    * @param value
    */
    setOpenOrderDetail(value: boolean) {
        this.isOpenOrderDetail = value
        if (!value) {
            this.orderDetailSummary = null
            this.orderDetailDish = []
        }
    }

    /**
     * 打开详情时写入列表行标识；摘要先展示已有字段，接口返回后再合并。
     * @param value 列表 stub
     */
    setOrderDetailData(value: Order) {
        this.orderDetailData = value
        this.orderDetailSummary = cloneOrderDetailTableRowFallback(value)
    }

    /**
     * 详情摘要（接口 order_head）
     * @param value 映射后的 Order
     */
    setOrderDetailSummary(value: Order | null) {
        this.orderDetailSummary = value
    }

    /**
     * 详情loading
     * @param value
     */
    setDetailLoading(value: boolean) {
        this.detailLoading = value
    }

    /**
     * 详情菜品
     * @param value
     */
    setOrderDetailDish(value: Dish[]) {
        this.orderDetailDish = value
    }

    /** 与表格 `renderFunction` 一致：有映射时显示 i18n，否则原字段 */
    private formatOperationNameForExport(item: DataAnalysis): string {
        const t = item.operation_type;
        if (t != null && SENSITIVE_OPERATION_TYPE_I18N[t]) {
            return i18n.t(SENSITIVE_OPERATION_TYPE_I18N[t]);
        }
        return String(item.operation_name ?? '');
    }

    /** 与表格列一致：非 SHIFT 才带详情正文 */
    private formatOperationDetailForExport(item: DataAnalysis): string {
        if (isSensitiveShiftOperation(item)) {
            return '';
        }
        return String(item.operation_detail ?? '');
    }

    /**
     * 云敏感操作记录：导出与列表列一致的 xlsx
     */
    downloadExcel(pageType: PageType) {
        if (pageType !== 'CLOUD') {
            return;
        }
        if (!this.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return;
        }
        const header: string[][] = [[
            i18n.t('business_analysis_page_employee_name'),
            i18n.t('business_analysis_page_employee_id'),
            i18n.t('business_analysis_page_order_head_id'),
            i18n.t('business_analysis_page_operation_time'),
            i18n.t('business_analysis_page_operation_name'),
            i18n.t('business_analysis_page_operation_detail'),
        ]];
        const rows = this.data.map((item) => [
            String(item.employee_name ?? ''),
            String(item.employee_id ?? ''),
            String(item.order_head_id ?? ''),
            item.operation_time ? moment(item.operation_time).format('YYYY-MM-DD HH:mm:ss') : '',
            this.formatOperationNameForExport(item),
            this.formatOperationDetailForExport(item),
        ]);
        const wpx = [{ wpx: 120 }, { wpx: 100 }, { wpx: 120 }, { wpx: 160 }, { wpx: 200 }, { wpx: 240 }];
        exportExcel(
            header.concat(rows),
            `${i18n.t('business_analysis_page_employee_sensitive_operation_record')}.xlsx`,
            wpx
        );
    }
}

export default SensitiveDataStore


// 页面类型
export type PageType = 'CLOUD' | 'INSTANT'